import { useEffect, useState } from 'react';
import { api, uid, ApiError } from '../lib/api';
import { qtyLine } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { SignaturePad } from '../components/SignaturePad';
import type { Client } from './Clients';

interface PendingIssue { id: string; dst: string; item: string; qty: number; }

export function Issues() {
  const { canAny } = useAuth();
  const toast = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState('');
  const [bonPM, setBonPM] = useState('');
  const [bonGM, setBonGM] = useState('');
  const [qtyPM, setQtyPM] = useState('');
  const [qtyGM, setQtyGM] = useState('');
  const [ref, setRef] = useState('');
  const [showBonScanner, setShowBonScanner] = useState(false);
  const [bonPhotoId, setBonPhotoId] = useState<string | null>(null);
  const [bonPreview, setBonPreview] = useState<string | null>(null);
  const [bonBusy, setBonBusy] = useState(false);
  const [bonResult, setBonResult] = useState<React.ReactNode>(null);
  const [formMsg, setFormMsg] = useState<{ text: string; err: boolean } | null>(null);
  const [pending, setPending] = useState<PendingIssue[] | null>(null);
  const [driverName, setDriverName] = useState('');
  const [driverCin, setDriverCin] = useState('');
  const [signatureBlob, setSignatureBlob] = useState<Blob | null>(null);
  const [sigKey, setSigKey] = useState(0);

  useEffect(() => {
    api<Client[]>('/v1/clients').then(setClients).catch(() => {});
    loadPending();
  }, []);

  async function loadPending() {
    try { setPending(await api<PendingIssue[]>('/v1/issues/pending')); }
    catch { setPending([]); }
  }

  async function suggest() {
    if (!clientId) { toast('Choisissez un client', true); return; }
    const bon: Record<string, number> = {};
    if (+bonPM > 0) bon.PM = +bonPM;
    if (+bonGM > 0) bon.GM = +bonGM;
    try {
      const d = await api<{ suggested?: Record<string, number> }>('/v1/issues/suggest', { method: 'POST', body: { client_id: clientId, bon } });
      setQtyPM(String(d.suggested?.PM ?? 0));
      setQtyGM(String(d.suggested?.GM ?? 0));
      toast('Suggestion appliquée');
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }

  async function onBonPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const id = uid();
    setBonPreview(URL.createObjectURL(file));
    setBonBusy(true);
    try {
      await api(`/v1/photos?id=${encodeURIComponent(id)}&kind=bon`, {
        method: 'POST', raw: true, headers: { 'Content-Type': file.type || 'image/jpeg' }, body: file,
      });
      setBonPhotoId(id);
    } catch (e) { toast('Envoi de la photo impossible : ' + (e instanceof ApiError ? e.message : ''), true); }
    finally { setBonBusy(false); }
  }

  async function analyzeBon() {
    if (!bonPhotoId) return;
    setBonResult(<p className="muted">Analyse en cours...</p>);
    try {
      const d = await api<{ client_text?: string; client_id?: string; quantities?: Record<string, number>; confidence?: number; legible?: boolean }>('/v1/vision/bon', { method: 'POST', body: { photo_id: bonPhotoId } });
      setBonResult(
        <div className="ai-result">
          <div className="aititle">Résultat</div>
          <p style={{ margin: '0 0 6px' }}>Client détecté : <strong>{d.client_text || '—'}</strong>{' '}
            {d.client_id ? <span className="badge ok">reconnu</span> : <span className="badge warn">à vérifier</span>}</p>
          <p style={{ margin: '0 0 6px' }}>Quantités lues : {qtyLine(d.quantities)}</p>
          <p className="muted" style={{ margin: 0 }}>Confiance : {d.confidence != null ? Math.round(d.confidence * 100) + '%' : '—'}
            {d.legible === false && <> · <span className="badge crit">illisible</span></>}</p>
        </div>
      );
      if (d.client_id) setClientId(d.client_id);
      if (d.quantities) { setBonPM(String(d.quantities.PM ?? '')); setBonGM(String(d.quantities.GM ?? '')); }
      toast("Lecture IA terminée — vérifiez avant d'envoyer");
    } catch (e) { setBonResult(<span className="err">{e instanceof ApiError ? e.message : 'Erreur'}</span>); }
  }

  async function createIssue(e: React.FormEvent) {
    e.preventDefault();
    const quantities: Record<string, number> = {};
    if (+qtyPM > 0) quantities.PM = +qtyPM;
    if (+qtyGM > 0) quantities.GM = +qtyGM;
    if (!clientId || !Object.keys(quantities).length) { setFormMsg({ text: 'Client et quantité obligatoires.', err: true }); return; }
    const bon: Record<string, number> = {};
    if (+bonPM > 0) bon.PM = +bonPM;
    if (+bonGM > 0) bon.GM = +bonGM;
    const myRef = ref.trim() || uid();
    const body: Record<string, unknown> = { client_id: clientId, quantities, ref: myRef };
    if (Object.keys(bon).length) body.bon = bon;
    if (driverName.trim()) body.driver_name = driverName.trim();
    if (driverCin.trim()) body.driver_cin = driverCin.trim();
    if (signatureBlob) {
      try {
        const sigId = uid();
        await api(`/v1/photos?id=${encodeURIComponent(sigId)}&kind=signature`, {
          method: 'POST', raw: true, headers: { 'Content-Type': 'image/png' }, body: signatureBlob,
        });
        body.signature_photo_id = sigId;
      } catch (e) {
        toast('Envoi de la signature impossible, remise envoyée sans signature : ' + (e instanceof ApiError ? e.message : ''), true);
      }
    }
    const clientName = clients.find((c) => c.id === clientId)?.name || clientId;
    try {
      const r = await api<{ queued?: true }>('/v1/issues', {
        method: 'POST', body, offlineQueue: true, queueKind: 'issue', queueLabel: `Remise — ${clientName}`,
      });
      if (r.queued) {
        setFormMsg({ text: 'Hors ligne — demande enregistrée, sera envoyée automatiquement.', err: false });
        toast('Demande de remise mise en attente (hors ligne)');
      } else {
        setFormMsg({ text: 'Demande envoyée, en attente de validation.', err: false });
        toast('Demande de remise envoyée');
        setDriverName(''); setDriverCin(''); setSignatureBlob(null); setSigKey((k) => k + 1);
        loadPending();
      }
    } catch (e) { setFormMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
  }

  async function validateOne(id: string) {
    try { await api('/v1/issues/validate', { method: 'POST', body: { ids: [id] } }); loadPending(); toast('Remise validée'); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }
  async function rejectOne(id: string) {
    const reason = prompt('Motif du refus :');
    if (reason === null) return;
    try { await api('/v1/issues/reject', { method: 'POST', body: { ids: [id], reason } }); loadPending(); toast('Remise refusée'); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }

  return (
    <>
      <div className="pagehead"><h1>Remises</h1></div>
      <div className="card">
        <h2>Demander une remise</h2>
        <form onSubmit={createIssue}>
          <label htmlFor="issClient">Client</label>
          <select id="issClient" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">—</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="row2">
            <div><label>Bon du jour — PM</label><input type="number" min={0} value={bonPM} onChange={(e) => setBonPM(e.target.value)} /></div>
            <div><label>Bon du jour — GM</label><input type="number" min={0} value={bonGM} onChange={(e) => setBonGM(e.target.value)} /></div>
          </div>
          <div className="row" style={{ marginTop: '0.4rem' }}>
            <button type="button" className="small" onClick={suggest}>Suggérer les quantités</button>
            <button type="button" className="small ai" onClick={() => setShowBonScanner((s) => !s)}>Scanner le bon</button>
          </div>
          {showBonScanner && (
            <div className="ai-result">
              <div className="aititle">Lecture du bon par IA</div>
              <input type="file" accept="image/*" capture="environment" onChange={onBonPhoto} />
              {bonPreview && <img src={bonPreview} className="photo-preview" />}
              <div className="row" style={{ marginTop: '0.6rem' }}>
                <button type="button" className="ai small" onClick={analyzeBon} disabled={!bonPhotoId || bonBusy}>Analyser avec l'IA</button>
              </div>
              {bonResult}
            </div>
          )}
          <div className="row2" style={{ marginTop: '0.6rem' }}>
            <div><label>Quantité à remettre — PM</label><input type="number" min={0} value={qtyPM} onChange={(e) => setQtyPM(e.target.value)} /></div>
            <div><label>Quantité à remettre — GM</label><input type="number" min={0} value={qtyGM} onChange={(e) => setQtyGM(e.target.value)} /></div>
          </div>
          <label>Référence (optionnel)</label>
          <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="évite les doublons si renvoyé" />

          <h3 style={{ marginTop: '1rem' }}>Preuve de remise (cahier numérique)</h3>
          <div className="row2">
            <div><label>Nom du chauffeur</label><input value={driverName} onChange={(e) => setDriverName(e.target.value)} placeholder="Nom, prénom" /></div>
            <div><label>CIN du chauffeur</label><input value={driverCin} onChange={(e) => setDriverCin(e.target.value)} placeholder="ex. AB123456" /></div>
          </div>
          <label>Signature du chauffeur</label>
          <SignaturePad key={sigKey} onChange={setSignatureBlob} />

          <div className="row" style={{ marginTop: '0.8rem' }}><button type="submit" className="primary">Envoyer la demande</button></div>
          {formMsg && <div className={formMsg.err ? 'err' : 'ok'}>{formMsg.text}</div>}
        </form>
      </div>
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>Remises en attente</h2>
          <button className="small" onClick={loadPending}>Actualiser</button>
        </div>
        {!pending && <div className="muted">Chargement...</div>}
        {pending && pending.length === 0 && <span className="muted">Aucune remise en attente.</span>}
        {pending && pending.length > 0 && (
          <div className="tblwrap"><table><tbody>
            <tr><th>Client</th><th>Caisse</th><th>Qté</th><th></th></tr>
            {pending.map((m) => (
              <tr key={m.id}>
                <td>{m.dst}</td><td>{m.item}</td><td>{m.qty}</td>
                <td className="row">
                  {canAny(['validate']) ? (
                    <>
                      <button className="small" onClick={() => validateOne(m.id)}>Valider</button>
                      <button className="small danger-btn" onClick={() => rejectOne(m.id)}>Refuser</button>
                    </>
                  ) : <span className="muted">Validation réservée au magasinier</span>}
                </td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </div>
    </>
  );
}
